/**
 * Mission Control — status grammar & surface tokens.
 * Consistent status colors only: success | running | failed | policy | waiting | unknown.
 *
 * P3 Phase 2 — visual language consolidation: these now derive directly from
 * the canonical `COCKPIT_COLORS` palette (the same one used by 56+ other
 * files across Operate) instead of a separate `--mc-*` CSS-var namespace
 * with its own hardcoded hex fallbacks. This is the single, surgical fix
 * that makes Executions/Executors/Certification (the pages built on this
 * `mission-control` kit) render with the exact same status colors,
 * typography scale, and spacing as every other Operate tab — without
 * requiring a rewrite of those pages' JSX, since they only ever consume
 * these tokens through `Panel`/`MetricCard`/`StatusBadge`/`SectionLabel`.
 */
import { COCKPIT_COLORS, safeAlpha } from '../design-system/tokens';

/**
 * success — done · running — in progress · failed · policy — governance hold
 * waiting — needs a person / blocked on setup (amber) · queued — open, pending, not started (neutral)
 */
export type McStatus = 'success' | 'running' | 'failed' | 'policy' | 'waiting' | 'queued' | 'unknown';

export const MC_STATUS: Record<
  McStatus,
  { label: string; color: string; muted: string; subtle: string }
> = {
  success: {
    label: 'Success',
    color: COCKPIT_COLORS.verified,
    muted: COCKPIT_COLORS.verifiedMuted,
    subtle: COCKPIT_COLORS.verifiedSubtle,
  },
  running: {
    label: 'Running',
    color: COCKPIT_COLORS.info,
    muted: COCKPIT_COLORS.infoMuted,
    subtle: COCKPIT_COLORS.infoSubtle,
  },
  failed: {
    label: 'Failed',
    color: COCKPIT_COLORS.critical,
    muted: COCKPIT_COLORS.criticalMuted,
    subtle: COCKPIT_COLORS.criticalSubtle,
  },
  policy: {
    label: 'Policy',
    color: COCKPIT_COLORS.violet,
    muted: safeAlpha(COCKPIT_COLORS.violet, 0.16),
    subtle: safeAlpha(COCKPIT_COLORS.violet, 0.07),
  },
  waiting: {
    label: 'Waiting',
    color: COCKPIT_COLORS.running,
    muted: COCKPIT_COLORS.runningMuted,
    subtle: COCKPIT_COLORS.runningSubtle,
  },
  queued: {
    label: 'Queued',
    color: COCKPIT_COLORS.ghost,
    muted: safeAlpha(COCKPIT_COLORS.ghost, 0.16),
    subtle: safeAlpha(COCKPIT_COLORS.ghost, 0.07),
  },
  unknown: {
    label: 'Unknown',
    color: COCKPIT_COLORS.ghost,
    muted: safeAlpha(COCKPIT_COLORS.ghost, 0.16),
    subtle: safeAlpha(COCKPIT_COLORS.ghost, 0.07),
  },
};

export const MC_SURFACE = {
  page: 'var(--app-bg)',
  panel: 'var(--panel-bg)',
  elevated: 'var(--card-bg)',
  border: 'var(--border-default)',
  borderSubtle: 'var(--border-subtle)',
  ink: COCKPIT_COLORS.ink,
  secondary: COCKPIT_COLORS.secondary,
  tertiary: COCKPIT_COLORS.tertiary,
  hover: 'var(--hover-bg)',
} as const;

export function mapIncidentStatus(status?: string, stage?: string, closed?: boolean): McStatus {
  if (closed) return 'success';
  const s = (status || '').toLowerCase();
  const st = (stage || '').toLowerCase();
  if (s.includes('fail') || s.includes('error') || st.includes('fail')) return 'failed';
  if (s.includes('policy') || st.includes('policy')) return 'policy';
  if (['resolved', 'closed', 'verified', 'completed', 'complete'].includes(s) || st === 'closed') {
    return 'success';
  }
  if (
    [
      'investigating',
      'analyzing',
      'analyzed',
      'executing',
      'running',
      'remediat',
      'validat',
      'observ',
      'engineering',
      'in_progress',
      'in-progress',
    ].some((k) => s.includes(k) || st.includes(k))
  ) {
    return 'running';
  }
  if (['needs_human', 'needs_more_evidence', 'awaiting'].some((k) => s.includes(k) || st.includes(k))) {
    return 'waiting';
  }
  if (['pending', 'open', 'new', 'queued'].some((k) => s === k || s.includes(k) || st.includes(k))) {
    return 'queued';
  }
  // Empty status with a stage still maps above; truly blank → queued (open work), not Unknown.
  if (!s && !st) return 'queued';
  return 'unknown';
}

/** Human label for incident StatusBadge — avoids raw "Unknown" for common AMS statuses. */
export function incidentStatusLabel(status?: string, stage?: string, closed?: boolean): string {
  if (closed) return 'Resolved';
  const mapped = mapIncidentStatus(status, stage, closed);
  const s = (status || '').toLowerCase();
  const st = (stage || '').toLowerCase();
  if (mapped === 'failed') return 'Failed';
  if (mapped === 'policy') return 'Policy';
  if (mapped === 'success') return 'Resolved';
  if (s === 'analyzed' || st.includes('analyzed')) return 'Analyzed';
  if (s.includes('investigat') || st.includes('investigat')) return 'Investigating';
  if (s.includes('needs_human') || st.includes('needs_human')) return 'Needs you';
  if (mapped === 'running') return 'In progress';
  if (mapped === 'waiting') return s.includes('evidence') || st.includes('evidence') ? 'Needs evidence' : 'Awaiting input';
  if (mapped === 'queued') {
    if (['open', 'new', 'pending', ''].includes(s)) return 'Open';
    return 'Queued';
  }
  return 'Unknown';
}

export function mapExecutionPhase(phase?: string, status?: string): McStatus {
  const p = (status || phase || '').toLowerCase();
  if (p.includes('policy')) return 'policy';
  if (['completed', 'passed', 'success', 'closed'].some((k) => p.includes(k))) return 'success';
  if (['failed', 'error', 'rolled_back'].some((k) => p.includes(k))) return 'failed';
  if (['running', 'executing', 'validating', 'observing', 'preparing', 'started'].some((k) => p.includes(k))) {
    return 'running';
  }
  if (p.includes('waiting') || p.includes('await')) return 'waiting';
  if (p.includes('pending') || p.includes('queued')) return 'queued';
  return 'unknown';
}
